import * as React from "react"
import { type ApolloError } from "@apollo/client"
import {
  type ReadInvitationRecipientQuery,
  type Session,
} from "@digits-graphql/frontend/graphql-public"
import { logInErrorText } from "@digits-shared/helpers/logInErrors"
import useSession from "@digits-shared/hooks/useSession"
import { jwtParse } from "@digits-shared/session/jwt/jwtParser"
import { useLocationRedirect } from "src/frontend/components/Public/hooks/useLocationRedirect"
import { useReadInvitationRecipient } from "src/frontend/components/Public/hooks/useReadInvitationRecipient"
import { useOnboardingContext } from "src/frontend/components/Public/Onboarding/OnboardingContext"
import { sessionError } from "src/frontend/components/Session/Glow/error"
import { useChromeAlerts } from "src/frontend/hooks/useChromeAlerts"
import type FrontendSession from "src/frontend/session"

interface LogInSession {
  logInOAuth?: Session
  logInMagicLink?: Session
  logInEmail?: Session
}

export function useSignIn() {
  const session = useSession<FrontendSession>()
  const {
    dispatch,
    state: { blockLoginRedirect },
  } = useOnboardingContext()
  const [loggedInUserEmail, setLoggedInUserEmail] = React.useState<string | undefined>(undefined)
  const submittedOnce = React.useRef(false)
  const setSubmitted = React.useCallback(() => {
    submittedOnce.current = true
  }, [])
  const { redirect } = useLocationRedirect()
  const { recipientData } = useReadInvitationRecipient()
  const { alertError } = useChromeAlerts()

  const onSignInCompleted = React.useCallback(
    async (logIn: LogInSession) => {
      setSubmitted()
      const isMagicLinkLogIn = Boolean(logIn.logInMagicLink)
      const newSession = logIn.logInOAuth || logIn.logInMagicLink || logIn.logInEmail
      const bearer = newSession?.bearer

      if (!bearer) {
        return TrackJS?.console.error("No session in LogInOAuthMutation response")
      }

      const identityMismatch = (userEmail?: string, data?: ReadInvitationRecipientQuery) =>
        !!userEmail && data && data.readInvitationRecipient.emailAddress !== loggedInUserEmail

      const { decodedSignedJWT } = await jwtParse(bearer)
      const userEmail = decodedSignedJWT?.user?.email
      setLoggedInUserEmail(userEmail)

      // Do not update session and redirect app if logged-in user identity differs from the invitation recipient,
      // but enable Google login again.
      if (identityMismatch(userEmail, recipientData)) {
        return
      }

      await session.onBearerChange(newSession)

      if (blockLoginRedirect) {
        return
      }

      // redirect to previous unauthenticated location
      redirect({ clearMagicLinkToken: isMagicLinkLogIn })
    },
    [loggedInUserEmail, recipientData, redirect, session, setSubmitted, blockLoginRedirect]
  )

  const onSignInError = React.useCallback(
    (error: ApolloError) => {
      setSubmitted()
      alertError(sessionError)
      dispatch({ type: "SET_ERROR_TEXT", text: logInErrorText(error) })
      dispatch({ type: "SET_LOADING", loading: false })
    },
    [dispatch, setSubmitted, alertError]
  )

  return React.useMemo(
    () => ({
      onSignInCompleted,
      onSignInError,
      loggedInUserEmail,
      submittedOnce,
    }),
    [onSignInCompleted, onSignInError, loggedInUserEmail]
  )
}
